import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, ShieldAlert, X } from "lucide-react";

/**
 * NotificationPrePromptModal
 * Mostra una mini-demo ORIO di come appare una notifica push,
 * prima di chiedere il permesso nativo del browser.
 * Aumenta l'opt-in rate evitando il classico prompt "freddo" del browser.
 *
 * Props:
 *  - open: boolean
 *  - onAccept: () => void   (chiamato quando l'utente conferma → il chiamante triggerà il permesso nativo)
 *  - onLater: () => void    (chiamato se l'utente rifiuta / chiude)
 */
export default function NotificationPrePromptModal({ open, onAccept, onLater }) {
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowToast(false);
      return;
    }
    const t = setTimeout(() => setShowToast(true), 350);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="notif-preprompt-overlay"
          className="fixed inset-0 z-[1800] flex items-end sm:items-center justify-center px-4 sm:p-6"
          style={{
            background: "rgba(20,14,8,0.55)",
            backdropFilter: "blur(8px)",
            // Extra bottom padding on mobile so the sheet sits ABOVE the fixed
            // MobileNav (which is z-[400], ~64px tall) + safe-area inset.
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            data-testid="notif-preprompt-sheet"
            className="w-full max-w-md rounded-3xl overflow-hidden border"
            style={{
              background: "#FFFFFF",
              borderColor: "#F0EBE3",
              boxShadow: "0 30px 80px -20px rgba(0,0,0,0.35)",
            }}
            initial={{ y: 60, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          >
            {/* Header preview area — sfondo warm con la mock-notifica */}
            <div
              className="relative px-5 pt-6 pb-8"
              style={{
                background:
                  "linear-gradient(135deg, #FFF4E5 0%, #FFE6CC 45%, #FFD6B0 100%)",
              }}
            >
              <button
                data-testid="notif-preprompt-close"
                onClick={onLater}
                className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.7)", color: "#5C5C5C" }}
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="text-[11px] uppercase tracking-[0.22em] text-[#9A6A2E] font-semibold">
                Anteprima
              </div>
              <div className="text-[#1A1A1A] font-display text-[22px] sm:text-[24px] leading-tight mt-1">
                Vedrai così quando un pericolo è vicino.
              </div>

              {/* Mock notification toast */}
              <div className="mt-5 min-h-[88px]">
                <AnimatePresence>
                  {showToast && (
                    <motion.div
                      data-testid="notif-preprompt-toast"
                      initial={{ y: -40, opacity: 0, scale: 0.95 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      exit={{ y: -40, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 380, damping: 26 }}
                      className="rounded-2xl border bg-white p-3 flex items-start gap-3"
                      style={{
                        borderColor: "#F0EBE3",
                        boxShadow:
                          "0 16px 40px -12px rgba(0,0,0,0.18), 0 4px 10px -4px rgba(0,0,0,0.06)",
                      }}
                    >
                      <div
                        className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                          background:
                            "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
                          color: "#FFFFFF",
                          boxShadow: "0 8px 18px -6px rgba(255,92,42,0.55)",
                        }}
                      >
                        <ShieldAlert className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[12px] font-semibold text-[#1A1A1A] uppercase tracking-wide">
                            ORIO
                          </div>
                          <div className="text-[10px] text-[#9A9A9A]">adesso</div>
                        </div>
                        <div className="text-[14px] font-semibold text-[#1A1A1A] leading-snug mt-0.5">
                          Pericolo segnalato a 1,2 km
                        </div>
                        <div className="text-[12px] text-[#6B6B6B] leading-snug">
                          Esca avvelenata sospetta — Parco del Valentino
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 pt-5 pb-5">
              <div className="flex items-start gap-3">
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(255,122,26,0.12)",
                    color: "#C25612",
                  }}
                >
                  <Bell className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1A1A1A]">
                    Attiva le notifiche di sicurezza
                  </div>
                  <div className="text-xs text-[#6B6B6B] leading-relaxed mt-1">
                    Riceverai un avviso solo quando serve davvero: pericoli entro
                    5 km, SOS nelle vicinanze, missioni urgenti in zona e
                    scadenze sanitarie critiche.
                  </div>
                </div>
              </div>

              <ul className="mt-4 space-y-2 text-xs text-[#5C5C5C]">
                <Bullet>Solo eventi importanti — niente spam.</Bullet>
                <Bullet>Puoi disattivarle in qualsiasi momento.</Bullet>
                <Bullet>Funzionano anche con l’app chiusa.</Bullet>
              </ul>

              <div className="mt-5 flex flex-col gap-2">
                <button
                  data-testid="notif-preprompt-accept"
                  onClick={onAccept}
                  className="w-full px-5 py-3 rounded-full font-semibold text-white text-[14px] active:scale-[0.98] transition-transform"
                  style={{
                    background:
                      "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
                    boxShadow: "0 12px 26px -10px rgba(255,92,42,0.55)",
                  }}
                >
                  Sì, attiva le notifiche
                </button>
                <button
                  data-testid="notif-preprompt-later"
                  onClick={onLater}
                  className="w-full px-5 py-2.5 rounded-full font-medium text-[13px]"
                  style={{ color: "#7A7A7A", background: "transparent" }}
                >
                  Più tardi
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span
        aria-hidden
        className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
        style={{ background: "#2DBA60" }}
      />
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
